/**
 * Zod-compatible validators for Chèques & Traites domain.
 * Pure TypeScript — no I/O, no framework dependencies.
 */

import { z } from 'zod/v4';
import { initialState } from './state-machine';

// ---- Primitive validators ----

/** Chèque number: at least 1 non-blank character */
export const checkNumberSchema = z.string().min(1, 'checks.validation.numberRequired').trim();

/** Amount must be strictly positive (millimes → 3 decimals) */
export const checkAmountSchema = z
  .number({ message: 'checks.validation.amountRequired' })
  .positive('checks.validation.amountPositive');

/** Date: YYYY-MM-DD format */
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
export const isoDateSchema = z.string().regex(isoDateRegex, 'checks.validation.invalidDate');

// ---- Composite check: issue date ≤ due date ----

export function validateDateCoherence(
  issueDate: string,
  dueDate: string,
): boolean {
  return issueDate <= dueDate;
}

// ---- Full form schema (create) ----

export const createCheckDraftSchema = z.object({
  type:            z.enum(['check', 'draft']),
  direction:       z.enum(['received', 'issued']),
  number:          checkNumberSchema,
  bankName:        z.string().default(''),  // free-text payer's bank (legacy, kept for back-compat)
  bankAccountId:   z.string().uuid().nullable().optional(),
  payerBankId:     z.string().uuid().nullable().optional(),
  payerRib:        z.string().max(50).nullable().optional(),
  beneficiaryName: z.string().max(255).optional(),
  placeOfPayment:  z.string().max(255).optional(),
  placeOfIssue:    z.string().max(255).optional(),
  protestable:     z.boolean().default(false),
  amount:          checkAmountSchema,
  issueDate:       isoDateSchema,
  dueDate:         isoDateSchema,
  acceptanceDate:  isoDateSchema.nullable().optional(),
  thirdPartyType:  z.enum(['client', 'supplier']),
  thirdPartyId:    z.string().min(1, 'checks.validation.thirdPartyRequired'),
  notes:           z.string().optional().default(''),
}).refine(
  (d) => validateDateCoherence(d.issueDate, d.dueDate),
  { message: 'checks.validation.dueDateAfterIssue', path: ['dueDate'] },
).refine(
  // bank_account_id is required for BOTH directions — it's OUR bank account:
  // - issued: the account the check is drawn on
  // - received: the account where we'll deposit/cash it
  // Without this, bank_transactions can't be auto-created on cashed/debited.
  (d) => !!d.bankAccountId,
  { message: 'checks.validation.bankAccountRequired', path: ['bankAccountId'] },
);

export type CreateCheckDraftFormData = z.infer<typeof createCheckDraftSchema>;

// ---- Transition event schema ----

export const transitionEventSchema = z.object({
  eventDate: isoDateSchema,
  notes:     z.string().optional().default(''),
});

export type TransitionEventFormData = z.infer<typeof transitionEventSchema>;

// ---- Helper: default initial state from direction ----
export { initialState };
